import type { SVGProps } from 'react';

interface BrandIconProps extends SVGProps<SVGSVGElement> {
  size?: number;
  /** Accessible name; icons are decorative (aria-hidden) when omitted. */
  title?: string;
}

/**
 * CloudBuck coin (brief §4): circle r=9 stroke 2 containing the finder eye at
 * 10 units wide, on Lucide's 24-grid. The only icon allowed a gradient fill.
 */
export function CoinIcon({ size = 20, title, gradient = true, ...rest }: BrandIconProps & { gradient?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      data-testid="ec-coin"
      {...rest}
    >
      {gradient && (
        <defs>
          <linearGradient id="ec-coin-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="oklch(0.88 0.14 92)" />
            <stop offset="1" stopColor="oklch(0.74 0.16 70)" />
          </linearGradient>
        </defs>
      )}
      <circle cx="12" cy="12" r="9" fill={gradient ? 'url(#ec-coin-gradient)' : 'none'} stroke={gradient ? 'none' : 'currentColor'} strokeWidth="2" />
      {/* finder eye, 10 units wide */}
      <rect x="7" y="7" width="10" height="10" rx="3" fill="none" stroke={gradient ? 'oklch(0.35 0.07 80)' : 'currentColor'} strokeWidth="2" />
      <rect x="10" y="10" width="4" height="4" rx="1.2" fill={gradient ? 'oklch(0.35 0.07 80)' : 'currentColor'} />
    </svg>
  );
}

/**
 * Lock-on frame (brief §4): the four corner brackets in one 24-grid glyph.
 * Used for the Scan nav action and scanner affordances. Takes currentColor.
 */
export function LockOnIcon({ size = 24, title, ...rest }: BrandIconProps) {
  const d = [
    'M4 9 V6 a2 2 0 0 1 2 -2 H9', // top-left
    'M15 4 h3 a2 2 0 0 1 2 2 v3', // top-right
    'M20 15 v3 a2 2 0 0 1 -2 2 h-3', // bottom-right
    'M9 20 H6 a2 2 0 0 1 -2 -2 v-3', // bottom-left
  ].join(' ');
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      data-testid="ec-lockon"
      {...rest}
    >
      <path d={d} />
    </svg>
  );
}
